import { InfiniteData, QueryClient, QueryKey, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import {
  DEFAULT_FETCH_DEBOUNCE,
  NOTIFICATION_PAGE_SIZE,
  QUERY_KEY,
  TANSTACK_QUERY_DEFAULT_GC_TIME,
  TANSTACK_QUERY_DEFAULT_STALE_TIME,
} from '@/lib/constants'
import {
  getAllNotifications,
  getUnreadNotificationCount,
  setNotificationAsRead,
  setNotificationAsUnread,
} from '@/services/notification-service'
import { AvailableLocale, InfiniteQueryResponse } from '@/types/global'
import { NotificationItem, NotificationsFetchFilterApi } from '@/types/notification'

// @TODO: 2025/07/08 if there are no problems in the code, refactor to have a common code with other query files,
// e.g. query/core.ts which will contain some common code for infinite queries and mutations
// reason for not currently doing in this stage is that the use cases are still only few
// so we might encounter some edge cases that we need to handle which will make the core
// implementation more complex if we do it now

type GetPaginatedNotificationsQueryProps = {
  locale?: AvailableLocale
  filter?: Partial<NotificationsFetchFilterApi>
}
export function getPaginatedNotificationsQuery({ filter, locale }: GetPaginatedNotificationsQueryProps = {}) {
  return {
    queryKey: getNotificationQueryKey(locale, filter),
    queryFn: async ({ pageParam = 1 }): Promise<InfiniteQueryResponse<NotificationItem>> => {
      const { ok, data, message } = await getAllNotifications({
        ...stabilizeGetAllNotificationFilter(filter),
        page: pageParam,
        limit: NOTIFICATION_PAGE_SIZE,
      })

      if (!ok) {
        throw new Error(`Failed to fetch notifications: ${message}`)
      }

      return {
        items: data?.items ?? [],
        length: data?.length ?? 0,
        page: pageParam,
      }
    },
    initialPageParam: 1,
    placeholderData: {
      pages: [{ items: [], length: 0, page: 1 }],
      pageParams: [1],
    },
    getNextPageParam: (
      lastPage: InfiniteQueryResponse<NotificationItem>,
      allPages: InfiniteQueryResponse<NotificationItem>[],
    ) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0)

      return totalFetched >= lastPage.length ? undefined : lastPage.page + 1
    },
  }
}

export const useInfiniteNotificationsQuery = ({ filter, locale }: GetPaginatedNotificationsQueryProps = {}) => {
  return useInfiniteQuery(getPaginatedNotificationsQuery({ filter, locale }))
}

type NotificationUpdateReadMutationProps = {
  id: Parameters<typeof setNotificationAsRead>[0]
}
export const useNotificationUpdateReadMutation = (queryClient: QueryClient) => {
  // Use the existing helper hook for query invalidation
  const { invalidate, debouncedInvalidate } = useNotificationQueryInvalidator(queryClient)

  return useMutation({
    mutationFn: async ({ id }: NotificationUpdateReadMutationProps) => {
      return await setNotificationAsRead(id)
    },

    onMutate: async ({ id }: NotificationUpdateReadMutationProps) => {
      // cancel ongoing fetches/refetches so it won't update the optimistically updated cache
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.NOTIFICATIONS] })

      // get all notifications query
      const allNotificationsQuery = queryClient.getQueryCache().findAll({ queryKey: [QUERY_KEY.NOTIFICATIONS] })

      const previousStates: { key: QueryKey; data: InfiniteData<NotificationItem[]> }[] = []

      // loop each query and update their cache
      allNotificationsQuery.forEach((query) => {
        const queryKey = query.queryKey as ReturnType<typeof getNotificationQueryKey>
        const data = query.state.data as InfiniteData<NotificationItem[]> | undefined

        // if no previous data, do not proceed
        if (!data) return

        previousStates.push({
          key: queryKey,
          data,
        })

        let isNotificationToBeUpdatedAlreadyFound = false

        queryClient.setQueryData(queryKey, (old: InfiniteData<InfiniteQueryResponse<NotificationItem>>) => {
          if (!old || !old.pages) return old

          return {
            ...old,
            pages: old.pages.map((page) => {
              // if the filter in query is unreadOnly, should remove notification from the array
              const filter = queryKey[QUERY_KEY_FILTER_INDEX]
              const willRemoveItem = filter !== undefined && filter.isRead === false

              const resultPage = willRemoveItem
                ? {
                    ...page,
                    length: page.length - 1,
                  }
                : page

              // if notification to be updated is already found, return remaining data as is except for the updated page length
              if (isNotificationToBeUpdatedAlreadyFound) return resultPage

              // search for notification to be updated
              const notificationIndex = page.items.findIndex((item) => item._id === id)
              if (notificationIndex === -1) return resultPage

              isNotificationToBeUpdatedAlreadyFound = true

              // apply update to matching item
              const notificationToBeUpdated: NotificationItem = page.items[notificationIndex]!
              const updatedNotification = { ...notificationToBeUpdated, isRead: true }

              // copy the old page array and update the notification at the found index
              let updatedNotifications = [...page.items]
              updatedNotifications[notificationIndex] = updatedNotification

              if (willRemoveItem) {
                updatedNotifications.splice(notificationIndex, 1)
              }
              resultPage.items = updatedNotifications

              return resultPage
            }),
          }
        })
      })

      return {
        previousStates,
      }
    },

    onError: (_err, _variables, context) => {
      // rollback to previous state if error occured
      if (context?.previousStates) {
        context.previousStates.forEach((state) => {
          queryClient.setQueryData(state.key, state.data)
        })
      }
    },
    onSuccess: async () => {
      if (typeof window !== 'undefined') {
        debouncedInvalidate()
      } else {
        // server-side fallback
        invalidate()
      }
    },
  })
}

type NotificationUpdateUnreadMutationProps = {
  id: Parameters<typeof setNotificationAsUnread>[0]
}
export const useNotificationUpdateUnreadMutation = (queryClient: QueryClient) => {
  const { invalidate, debouncedInvalidate } = useNotificationQueryInvalidator(queryClient)

  return useMutation({
    mutationFn: async ({ id }: NotificationUpdateUnreadMutationProps) => {
      return await setNotificationAsUnread(id)
    },
    onMutate: async ({ id }: NotificationUpdateUnreadMutationProps) => {
      // cancel ongoing fetches/refetches so it won't update the optimistically updated cache
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.NOTIFICATIONS] })

      // get all notifications query
      const allNotificationsQuery = queryClient.getQueryCache().findAll({ queryKey: [QUERY_KEY.NOTIFICATIONS] })

      const previousStates: { key: QueryKey; data: InfiniteData<NotificationItem[]> }[] = []

      // loop each query and update their cache
      allNotificationsQuery.forEach((query) => {
        const queryKey = query.queryKey as QueryKey
        const data = query.state.data as InfiniteData<NotificationItem[]> | undefined

        // if no previous data, do not proceed
        if (!data) return

        previousStates.push({
          key: queryKey,
          data,
        })

        queryClient.setQueryData(queryKey, (old: InfiniteData<InfiniteQueryResponse<NotificationItem>>) => {
          if (!old || !old.pages) return old

          return {
            ...old,
            pages: old.pages.map((page) => {
              // search for notification to be updated
              const notificationIndex = page.items.findIndex((item) => item._id === id)
              if (notificationIndex === -1) return page

              // apply update to matching item
              const notificationToBeUpdated: NotificationItem = page.items[notificationIndex]!
              const updatedNotification = { ...notificationToBeUpdated, isRead: false }

              // copy the old page array and update the notification at the found index
              let updatedNotifications = [...page.items]
              updatedNotifications[notificationIndex] = updatedNotification

              return {
                ...page,
                items: updatedNotifications,
              }
            }),
          }
        })
      })

      return {
        previousStates,
      }
    },
    onError: (_err, _variables, context) => {
      // rollback to previous state if error occured
      if (context?.previousStates) {
        context.previousStates.forEach((state) => {
          queryClient.setQueryData(state.key, state.data)
        })
      }
    },
    onSuccess: async () => {
      // refetch all notifications query if a notification became unread
      // since it will be difficult to guess where to insert the unread notification
      // in some query cache where they do not exist
      // (e.g. cache with unread only filter and the unread notification was previously read notification before)

      if (typeof window !== 'undefined') {
        debouncedInvalidate()
      } else {
        // server-side fallback
        invalidate()
      }
    },
  })
}

// helper hook for invalidating notification queries outside of mutations
export const useNotificationQueryInvalidator = (queryClient: QueryClient) => {
  const invalidate = useCallback(async () => {
    // remove inactive queries first before invalidating
    queryClient.removeQueries({ queryKey: [QUERY_KEY.NOTIFICATIONS], type: 'inactive' })
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.NOTIFICATIONS] })
  }, [queryClient])

  const debouncedInvalidate = useDebouncedCallback(invalidate, DEFAULT_FETCH_DEBOUNCE)

  return { invalidate, debouncedInvalidate }
}

// stabilize the notification filter
export const stabilizeGetAllNotificationFilter = (
  filter?: Partial<NotificationsFetchFilterApi>,
): NotificationsFetchFilterApi => {
  return {
    ...(filter?.keyword && filter.keyword.trim().length > 1 ? { keyword: filter.keyword.trim().toLowerCase() } : {}),
    ...(filter?.sortBy ? { sortBy: filter.sortBy } : {}),
    ...(filter?.sortOrder ? { sortOrder: filter.sortOrder } : {}),
    ...(filter?.types && filter.types.length > 0 ? { types: filter.types } : {}),
    ...(filter && filter.isRead !== undefined && !filter.isRead ? { isRead: filter.isRead } : {}),
  }
}

// create a stabilized query key for fetching notifications with filter
const QUERY_KEY_FILTER_INDEX = 2
export function getNotificationQueryKey(locale: AvailableLocale = 'en', filter?: Partial<NotificationsFetchFilterApi>) {
  return [QUERY_KEY.NOTIFICATIONS, locale, stabilizeGetAllNotificationFilter(filter)] as const
}

export function getUnreadNotificationCountQuery() {
  return {
    queryKey: [QUERY_KEY.NOTIFICATIONS, 'unread-count'] as const,
    queryFn: async (): Promise<number> => {
      const { ok, data, message } = await getUnreadNotificationCount()

      if (!ok) {
        throw new Error(`Failed to fetch unread notification count: ${message}`)
      }

      return data ?? 0
    },
    staleTime: TANSTACK_QUERY_DEFAULT_STALE_TIME,
    gcTime: TANSTACK_QUERY_DEFAULT_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  }
}

export const useUnreadNotificationCountQuery = () => {
  return useQuery(getUnreadNotificationCountQuery())
}
